"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  GeneralResponse,
  UpdateCanvasEpisodeOfCareInput,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface UpdateCanvasEpisodeOfCareVariables {
  input: UpdateCanvasEpisodeOfCareInput;
}

interface UpdateCanvasEpisodeOfCareResponse {
  updateCanvasEpisodeOfCare: GeneralResponse;
}

interface UpdateCanvasEpisodeOfCareResponseType {
  data?: UpdateCanvasEpisodeOfCareResponse;
}

interface MutationError {
  message: string;
}

export const updateCanvasEpisodeOfCareMutation = gql`
  mutation updateCanvasEpisodeOfCare($input: UpdateCanvasEpisodeOfCareInput!) {
    updateCanvasEpisodeOfCare(input: $input) {
      success
      message
      errorMsg
    }
  }
`.loc?.source.body as GeneratedMutation<
  UpdateCanvasEpisodeOfCareVariables,
  UpdateCanvasEpisodeOfCareResponse
>;

/**
 * `updateCanvasEpisodeOfCare` takes exactly one action per call -- set one of
 * `updateDiagnosis`, `completeEpisodeOfCare` or `updateFollowUpCount` on the
 * input, never two. `noteId` is the DynamoDB `episodeOfCareId`: the Canvas note
 * is created with its `note_key` set to that id, so one id addresses both sides.
 *
 * Shared across apps (rmh-web's SOAP assessment and discharge modal, rmh-admin's
 * assigned-visits editor) rather than duplicated per app, so the one-action
 * invariant and the gql document live in a single place.
 */
export const useUpdateCanvasEpisodeOfCareMutation = (
  options: MutationOptions<
    UpdateCanvasEpisodeOfCareResponseType,
    MutationError,
    UpdateCanvasEpisodeOfCareVariables
  > = {}
) => {
  return useMutation<
    UpdateCanvasEpisodeOfCareResponseType,
    MutationError,
    UpdateCanvasEpisodeOfCareVariables
  >({
    ...options,
    // Same-scope mutations run serially in dispatch order; the backend
    // handlers are read-modify-write on one EoC row (ENG-763).
    scope: { id: "updateCanvasEpisodeOfCare" },
    mutationFn: async (variables) =>
      fetchGraphQLClient(updateCanvasEpisodeOfCareMutation, variables),
  });
};
